import { applySnapshot, Instance, types } from 'mobx-state-tree';
import {
  AttachEventParams,
  DynamicModalLayer,
  DynamicModalLayerInstance,
  IDynamicModalLayerModelIn,
} from '../_models/dynamic-modal-layer/dynamicModalLayer';
import { isPopupModal } from '@photosi/rn-components/modals/dynamic-modals/DynamicModal.utils';

export type DynamicModalInstance = Instance<typeof DynamicModal>;

// In the stackedModals there are 4 rules:
// 1. It can exist only one POPUP modal at a time, and there always be the last element of the array
// 2. if there is NOT POPUP modal, Only the last element of stackedModals is visible
// 3. if there is a POPUP modal, Only the last two elements of stackedModals are visible (FULLSCREEN modal in background - POPUP modal in foreground)
// 4. if there are multiple FULLSCREEN modals, only the last one is visible
export const DynamicModal = types
  .model({
    stackedModals: types.optional(types.array(DynamicModalLayer), []),
  })
  .views(self => ({
    get activeModal() {
      return self.stackedModals[self.stackedModals.length - 1];
    },
    get isModalVisible() {
      return self.stackedModals.length > 0;
    },
  }))
  .views(self => ({
    get isPopupModalVisible() {
      return self.isModalVisible && isPopupModal(self.activeModal?.dynamicModalView);
    },
  }))
  .actions(self => {
    return {
      closeModal: () => {
        self.stackedModals.pop();
      },

      closeVisibleModals: () => {
        const lastModal = self.stackedModals[self.stackedModals.length - 1];
        const penultimateModal = self.stackedModals[self.stackedModals.length - 2];
        if (penultimateModal && lastModal && isPopupModal(lastModal.dynamicModalView)) {
          self.stackedModals.pop();
        }
        self.stackedModals.pop();
      },
      openModal: (
        options: Omit<IDynamicModalLayerModelIn, 'id'> & AttachEventParams,
        avoidRemoveAllPopup?: boolean,
      ): DynamicModalLayerInstance => {
        let modalLayer: DynamicModalLayerInstance;
        const id = options.dynamicModalView ?? `dynamic-modal-layer-${new Date().getTime()}`;
        let existedModalLayerIndex = self.stackedModals.findIndex(modal => modal.id === id);
        if (existedModalLayerIndex >= 0 && existedModalLayerIndex === self.stackedModals.length - 1) {
          // if the modal layer is the last one, only update that one
          modalLayer = self.stackedModals[existedModalLayerIndex];
          applySnapshot(modalLayer, { ...options, id });
        } else {
          // in other cases...
          // first, remove all popup modals
          // avoidRemoveAllPopup è un workaround necessario nel caso in cui elimino un'immagine nel cassetto e devo mostrare un'altra modale sopra
          !avoidRemoveAllPopup &&
            self.stackedModals.forEach(modal => {
              if (isPopupModal(modal.dynamicModalView)) {
                self.stackedModals.remove(modal);
              }
            });

          existedModalLayerIndex = self.stackedModals.findIndex(modal => modal.id === id);
          if (existedModalLayerIndex >= 0 && existedModalLayerIndex === self.stackedModals.length - 1) {
            // if the modal layer is the last one, only update that one
            // it could happen if the user re-open a FULLSCREEN modal in background
            modalLayer = self.stackedModals[existedModalLayerIndex];
            applySnapshot(modalLayer, { ...options, id });
          } else {
            // in other cases, it needs to be change stackedModals order
            if (existedModalLayerIndex >= 0) {
              // it could happen if the user re-open an hidden FULLSCREEN modal
              modalLayer = self.stackedModals.splice(existedModalLayerIndex, 1)[0];
              applySnapshot(modalLayer, { ...options, id });
            } else {
              // it could happen if the user open for the first time a modal
              modalLayer = DynamicModalLayer.create({ ...options, id });
              modalLayer.init(options);
            }

            self.stackedModals.push(modalLayer);
          }
        }
        return modalLayer;
      },
    };
  });
